// Viewer relay tree for the mesh; see docs/repasse.md.

/** Viewers the publisher feeds itself, relays included. */
export const MAX_DIRECT = 4

/** Children per relay: each one is another encoder on the relay's machine. */
export const MAX_RELAY_CHILDREN = 3

export interface RelayCandidate {
    id: string
    /** The viewer said it can relay (desktop, enough cores, not sharing). */
    relayOk: boolean
    cores: number
    /** Its connection to the publisher is up. */
    connected: boolean
    /** The path to it goes through TURN. */
    viaTurn: boolean
}

export function pairKey(relay: string, child: string): string {
    return `${relay}>${child}`
}

/**
 * Child → relay for viewers that should get the stream from a relay; the rest
 * stay direct. Sticky: moving someone means renegotiating and a visible blink.
 */
export function planRelays(
    viewers: RelayCandidate[],
    previous: ReadonlyMap<string, string>,
    badPairs: ReadonlySet<string>
): Map<string, string> {
    const routes = new Map<string, string>()
    if (viewers.length <= MAX_DIRECT) return routes

    const wasRelay = new Set(previous.values())
    const order = new Map(viewers.map((viewer, index) => [viewer.id, index]))
    const eligible = viewers
        .filter((viewer) => viewer.relayOk && viewer.connected && !viewer.viaTurn)
        .sort((a, b) => (
            Number(wasRelay.has(b.id)) - Number(wasRelay.has(a.id))
            || b.cores - a.cores
            || order.get(a.id)! - order.get(b.id)!
        ))

    const overflow = viewers.length - MAX_DIRECT
    const wanted = Math.ceil(overflow / MAX_RELAY_CHILDREN)
    const relays = eligible.slice(0, Math.min(wanted, MAX_DIRECT)).map((viewer) => viewer.id)
    if (relays.length === 0) return routes

    const load = new Map(relays.map((id) => [id, 0]))
    const fits = (relay: string, child: string) => (
        (load.get(relay) ?? MAX_RELAY_CHILDREN) < MAX_RELAY_CHILDREN && !badPairs.has(pairKey(relay, child))
    )
    const assign = (child: string, relay: string) => {
        routes.set(child, relay)
        load.set(relay, (load.get(relay) ?? 0) + 1)
    }

    const isRelay = new Set(relays)
    const others = viewers.filter((viewer) => !isRelay.has(viewer.id))
    let remaining = overflow

    for (const viewer of others) {
        if (remaining <= 0) break
        const relay = previous.get(viewer.id)
        if (relay && fits(relay, viewer.id)) {
            assign(viewer.id, relay)
            remaining -= 1
        }
    }

    // Latest joiners go to a relay; whoever has been watching longest stays direct.
    for (const viewer of [...others].reverse()) {
        if (remaining <= 0) break
        if (routes.has(viewer.id)) continue
        const relay = relays
            .filter((id) => fits(id, viewer.id))
            .sort((a, b) => load.get(a)! - load.get(b)!)[0]
        if (!relay) continue
        assign(viewer.id, relay)
        remaining -= 1
    }
    return routes
}

export function localRelayCapacity(sharing: boolean, uploadLimited = false): { relayOk: boolean; cores: number } {
    const cores = typeof navigator === 'undefined' ? 0 : navigator.hardwareConcurrency || 0
    return { relayOk: !sharing && !uploadLimited && cores >= 4 && !isMobileDevice(), cores }
}

function isMobileDevice(): boolean {
    if (typeof navigator === 'undefined') return false
    const data = (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData
    if (typeof data?.mobile === 'boolean') return data.mobile
    return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent ?? '')
}

/** Lost `watch` signals heal within this time. */
export const WATCH_REANNOUNCE = 15_000

/** A stage tile id, `peerId:kind`, split back. */
export function parseWatchKey(key: string): { peerId: string; kind: 'screen' | 'camera' } | null {
    const at = key.lastIndexOf(':')
    const kind = key.slice(at + 1)
    if (at <= 0 || (kind !== 'screen' && kind !== 'camera')) return null
    return { peerId: key.slice(0, at), kind }
}

/** Id list from the network: short unique strings, capped. */
export function sanitizeIds(raw: unknown, max = 16): string[] {
    if (!Array.isArray(raw)) return []
    const ids = raw.filter((item): item is string => typeof item === 'string' && item.length > 0 && item.length <= 128)
    return [...new Set(ids)].slice(0, max)
}
